import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Logger from '../../../common/logger';
import {
    GroupPricingStructure,
    useArchiveLocationOfferMutation,
    useCreateLocationOfferMutation,
} from '../../../api/group/groupAdminApiSlice';
import CustomButton from '../../../components/CustomButton';
import { enqueueSuccessSnackbar } from '../../../action/snackbarCommands';
import CustomModal from '../../common/CustomModal';

interface Props {
    /** 'create' copies the default list for this cell's location;
     *  'remove' archives the location sheet, falling back to the default. */
    mode: 'create' | 'remove';
    group: GroupPricingStructure;
    onClose: () => void;
    onDone: () => void;
}

/**
 * Confirm and run a location-sheet action (pricing stream slice 2,
 * ADR-0025 amendment 2026-09-28). Same shape as ProductRetireDialog: the
 * dialog stays open on a failure and shows the server's message in place —
 * one message per cause, never a silent close (#2255).
 */
const LocationOfferDialog = ({ mode, group, onClose, onDone }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [createOffer, { isLoading: creating }] = useCreateLocationOfferMutation();
    const [archiveOffer, { isLoading: removing }] = useArchiveLocationOfferMutation();
    const [failure, setFailure] = useState<string | null>(null);

    const location = group.locationName ?? t('common.missing');
    const busy = creating || removing;

    const handleConfirm = async () => {
        setFailure(null);
        try {
            if (mode === 'create') {
                const result = await createOffer({
                    productId: group.productId!,
                    locationId: group.locationId!,
                }).unwrap();
                dispatch(enqueueSuccessSnackbar({
                    message: t('services.pricing.location_sheet.create_success', {
                        location,
                        count: result.copiedRows,
                    }),
                    autoHideDuration: 4000,
                }));
            } else {
                await archiveOffer({
                    productId: group.productId!,
                    offerId: group.offerId!,
                }).unwrap();
                dispatch(enqueueSuccessSnackbar({
                    message: t('services.pricing.location_sheet.remove_success', {
                        location,
                    }),
                    autoHideDuration: 4000,
                }));
            }
            onDone();
            onClose();
        } catch (err: any) {
            Logger('LocationOfferDialog').error(
                `Location sheet ${mode} failed: ${JSON.stringify(err)}`,
            );
            // The server's message names the cause (duplicate sheet, no
            // default list, unknown location) — show it, not a generic line.
            setFailure(
                err?.data?.message
                ?? t('services.pricing.location_sheet.generic_error'),
            );
        }
    };

    return (
        <CustomModal
            open={true}
            title={t(mode === 'create'
                ? 'services.pricing.location_sheet.create_title'
                : 'services.pricing.location_sheet.remove_title', { location })}
            description={group.name ?? ''}
            onClose={onClose}
            closeButton={
                <CustomButton
                    title={t('services.pricing.location_sheet.cancel')}
                    isFilled={false}
                    onClick={onClose}
                />
            }
            saveButton={
                <CustomButton
                    title={t(mode === 'create'
                        ? 'services.pricing.location_sheet.create_cta'
                        : 'services.pricing.location_sheet.remove_cta')}
                    loading={busy}
                    disabled={busy}
                    onClick={handleConfirm}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                <p className={'text-sm text-foreground/70'}>
                    {t(mode === 'create'
                        ? 'services.pricing.location_sheet.create_body'
                        : 'services.pricing.location_sheet.remove_body', { location })}
                </p>
                {failure && (
                    <div
                        className={'rounded-[5px] border border-foreground/15 bg-foreground/5'
                            + ' px-4 py-3 text-sm text-foreground/70'}
                        role={'alert'}
                    >
                        {failure}
                    </div>
                )}
            </div>
        </CustomModal>
    );
};

export default LocationOfferDialog;
